import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath, Paginated } from '../../../shared/api/types';
import { notifyOnError } from '../../../shared/api/onQueryError';
import type { TicketDto, TicketMessageDto } from '../model/dto';
import { mapTicket, mapTicketMessage } from '../model/mappers';
import type {
  CreateTicketPayload,
  Ticket,
  TicketMessage,
  SupportRequestsResponse,
  SupportRequestsParams,
  CreateGuestTicketPayload,
} from '../model/types';

/** Все ручки поддержки живут под этим префиксом — даже пользовательские. */
const BASE = '/api/admin/tech_supports/technical_support';

export interface TicketsPage {
  items: Ticket[];
  total: number;
  pages: number;
}

export const supportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Все обращения — для администратора. */
    getSupportRequests: builder.query<
      TicketsPage,
      { page_size?: number; page_num?: number; query?: string }
    >({
      query: (params) => ({
        url: BASE satisfies ApiPath,
        params: { page_size: 10, page_num: 1, query: '', ...params },
      }),
      transformResponse: (response: Paginated<TicketDto>): TicketsPage => ({
        items: (response.data ?? []).map(mapTicket),
        total: response.count ?? 0,
        pages: response.pages ?? 1,
      }),
      providesTags: ['SupportRequest'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить обращения');
      },
    }),

    /** Обращения текущего пользователя. */
    getMySupportRequests: builder.query<
      TicketsPage,
      { page_size?: number; page_num?: number; query?: string }
    >({
      query: (params) => ({
        url: `${BASE}/user` satisfies ApiPath,
        params: { page_size: 10, page_num: 1, query: '', ...params },
      }),
      transformResponse: (response: Paginated<TicketDto>): TicketsPage => ({
        items: (response.data ?? []).map(mapTicket),
        total: response.count ?? 0,
        pages: response.pages ?? 1,
      }),
      providesTags: ['SupportRequestsUser'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить ваши обращения');
      },
    }),

    getSupportChatsUser: builder.query<SupportRequestsResponse, SupportRequestsParams>({
      query: ({ page_num = 1, page_size = 10, query = '' }) => ({
        url: '/api/admin/tech_supports/technical_support/user',
        params: { page_size, page_num, query },
      }),
      providesTags: ['SupportRequestsUser'],
    }),

    getSupportRequest: builder.query<Ticket, string>({
      query: (id) => ({ url: `${BASE}/${id}` satisfies ApiPath }),
      transformResponse: (response: TicketDto) => mapTicket(response),
      providesTags: (_r, _e, id) => [{ type: 'SupportRequest', id }],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить обращение');
      },
    }),

    getSupportChat: builder.query<TicketMessage[], string>({
      query: (id) => ({
        url: `${BASE}/${id}/chat` satisfies ApiPath,
        params: { sort_by: 'created_at', sort_order: 'asc' },
      }),
      transformResponse: (response: { data?: TicketMessageDto[] }) =>
        (response.data ?? []).map(mapTicketMessage),
      providesTags: (_r, _e, id) => [{ type: 'SupportChat', id }],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось загрузить переписку');
      },
    }),

    createSupportRequest: builder.mutation<Ticket, CreateTicketPayload>({
      query: ({ email, name, organizationId, project_id, problem, description, comment, files }) => {
        const body = new FormData();

        body.append('email', email);
        body.append('name', name);
        body.append('organization_id', organizationId);
        body.append('problem', problem);
        body.append('description', description);
        body.append('comment', comment ?? '');

        body.append('project_id', project_id ?? '');
        (files ?? []).forEach((file) => body.append('files', file));

        return { url: BASE satisfies ApiPath, method: 'POST', body };
      },
      transformResponse: (response: TicketDto) => mapTicket(response),
      invalidatesTags: ['SupportRequest', 'SupportRequestsUser'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось отправить обращение');
      },
    }),

    sendSupportAnswer: builder.mutation<void, { requestId: string; text: string }>({
      query: ({ requestId, text }) => ({
        url: `${BASE}/${requestId}/chat` satisfies ApiPath,
        method: 'POST',
        body: { text },
      }),
      invalidatesTags: (_r, _e, { requestId }) => [{ type: 'SupportChat', id: requestId }],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось отправить ответ');
      },
    }),

    createGuestSupportRequest: builder.mutation<Ticket, CreateGuestTicketPayload>({
      query: ({ problem, email, description }) => {
        const body = new FormData();
        body.append('problem', problem);
        body.append('email', email);
        body.append('description', description);

        return { url: BASE satisfies ApiPath, method: 'POST', body };
      },
      transformResponse: (response: TicketDto) => mapTicket(response),
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось отправить обращение');
      },
    }),

    changeSupportRequest: builder.mutation<
      void,
      { requestId: string; body: { status?: string; criticality?: string; deadline?: string } }
    >({
      query: ({ requestId, body }) => ({
        url: `${BASE}/${requestId}` satisfies ApiPath,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { requestId }) => [
        { type: 'SupportRequest', id: requestId },
        'SupportRequest',
        'SupportRequestsUser',
      ],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось изменить заявку');
      },
    }),
  }),
  overrideExisting: false,
});

export const supportReportPath = (startDate: string, endDate: string): ApiPath =>
  `${BASE}/export_file?start_date=${startDate}&end_date=${endDate}` satisfies ApiPath;

export const {
  useGetSupportChatsUserQuery,
  useGetSupportRequestsQuery,
  useGetMySupportRequestsQuery,
  useGetSupportRequestQuery,
  useGetSupportChatQuery,
  useCreateSupportRequestMutation,
  useCreateGuestSupportRequestMutation,
  useSendSupportAnswerMutation,
  useChangeSupportRequestMutation,
} = supportApi;
